import { useSelector, useDispatch } from 'react-redux';
import ContactDetails from './ContactDetails/ContactDetails';
import WithNavigation from '../../layouts/WithNavigation';
import Loader from '../../components/Loader';
import { fetchFaqsNotesDocuments } from 'src/store/actions/faqsNotesDocuments';
import { clearServiceRequests } from 'src/store/actions/serviceRequests';
import { selectNotes } from 'src/store/selectors/faqsNotesDocuments';
import ContactTabs from 'src/pages/Contact/ContactTabs';
import ErrorDisplay from 'src/components/ErrorDisplay/ErrorDisplay';
import { useContactFilters } from 'src/pages/Contacts/CustomHooks/useContactFilters';
import { ContactFormProvider } from 'src/SharedCommonContext/ContactFormContext';
import { PropertiesForContactsProvider } from 'src/pages/Contacts/ContactsContext/PropertiesForContactsContext';
import { useEffect, useState } from 'react';
import {
  sendActivationLink,
  sendResetPasswordLink,
} from 'src/utils/CommonHelpers/ContactAPIOperations/commonContactDataHelpers';
import { useContactCounts } from 'src/pages/SharedPageCustomHooks/UseContactCounts/useContactCounts';
import {
  useContactFormSubmissionContacts
} from 'src/pages/SharedPageCustomHooks/UseContactSaveContactsPage/useContactFormSubmission';

interface Match {
  params: {
    id: string;
  };
}

interface Location {
  state?: {
    isFromDashBoard?: boolean;
  };
}

interface ContactProps {
  match: Match;
  location: Location;
  history: History;
}

const Contact = ({ match, location, history }: ContactProps) => {
  const [state, setState] = useState({
    noteVisibleToHomeownerOccupantRealtor: false,
  });

  const notes = useSelector(selectNotes);
  const dispatch = useDispatch();

  const contactId = match.params.id.split('&');

  // Todo: The logic for getting this state from the API > Redux is a cluster f*ck of spaghetti. We should fix this.
  const clearServiceRequestsAction = () => dispatch(clearServiceRequests());
  const onFetchNotesAndDocuments = (...args: any[]) =>
    dispatch(fetchFaqsNotesDocuments(...args));

  const {
    contactCounts,
    countsError,
    fetchContactCountsFromAPI
  } = useContactCounts();
  
  const { handleSubmitContactForm } = useContactFormSubmissionContacts({
    onSuccess: () => refetchSingleContactById(contactId[0]),
  });

  const { fetchContactById, currentContactById, refetchSingleContactById } =
    useContactFilters();

  useEffect(() => {
    const onLoadContactPage = async () => {
      let HomeOwnerRealtorNotes: any[] = [];

      if (notes != undefined && notes != '' && notes.length > 0) {
        let notesList: any = notes.filter(
          (x: any) => x.documentType == 'NOTES'
        );

        if (notesList.length > 0) {
          notesList.forEach((object: any) => {
            HomeOwnerRealtorNotes.push(
              JSON.parse(JSON.parse(JSON.stringify(object.data)))
            );
          });
        }

        if (HomeOwnerRealtorNotes.length > 0) {
          let filterNotes: any[] = HomeOwnerRealtorNotes.filter(
            (x: any) => x.noteVisibleToHomeownerOccupantRealtor == true
          );

          if (filterNotes.length > 0) {
            setState({ noteVisibleToHomeownerOccupantRealtor: true });
          }
        }
      }

      clearServiceRequestsAction();
      let contactId = match.params.id.split('&');
      await fetchContactById(contactId[0]);
    };

    onLoadContactPage();
  }, []);

  useEffect(() => {
    const loadContactCounts = async () => {
      if (
        currentContactById &&
        currentContactById.roles &&
        currentContactById.roles.length > 0
      ) {
        reFetchContactCountsFromAPI();
      }

      onFetchNotesAndDocuments(contactId[0]);
    };

    loadContactCounts();
  }, [currentContactById]);

  const reFetchContactCountsFromAPI = () => {
    fetchContactCountsFromAPI(
      contactId[0],
      currentContactById.roles[0].role.name
    );
  };

  const navFrom: string = contactId.length === 2 ? contactId[1] : '';

  let isFromDashBoard: boolean = !!(
    location &&
    location.state &&
    location.state.isFromDashBoard &&
    location.state.isFromDashBoard === true
  );

  let { noteVisibleToHomeownerOccupantRealtor } = state;

  if (countsError) {
    return (
      <ErrorDisplay
        title="Contact Error"
        message={countsError}
        onRetry={() => window.location.reload()}
        buttonText="Retry"
        colorScheme="red"
      />
    );
  }

  if (countsError) {
    return (
      <ErrorDisplay
        title="Counts Loading Error"
        message={countsError}
        onRetry={() =>
          fetchContactCountsFromAPI(
            contactId[0],
            currentContactById.roles[0].role.name
          )
        }
        buttonText="Retry Counts"
        colorScheme="yellow"
        pageTitle={`${currentContactById.firstname} ${currentContactById.lastname}`}
      />
    );
  }

  if (currentContactById && contactCounts) {
    return (
      <ContactFormProvider>
        <PropertiesForContactsProvider>
          <WithNavigation
            title={`${currentContactById.firstname} ${currentContactById.lastname}`}
            top={
              <ContactDetails
                contactData={currentContactById}
                onSubmitContactForm={handleSubmitContactForm}
                sendResetPasswordLinkToUser={sendResetPasswordLink}
                sendActivationLinkToUser={sendActivationLink}
                navFrom={navFrom}
                counts={contactCounts}
                isFromDashBoard={isFromDashBoard}
                refetchContactFormData={refetchSingleContactById}
              />
            }
            bottom={
              <ContactTabs
                contact={currentContactById}
                counts={contactCounts}
                noteVisibleToHomeownerOccupantRealtor={
                  noteVisibleToHomeownerOccupantRealtor
                }
                reFetchContactCountsFromAPI={reFetchContactCountsFromAPI}
              />
            }
          />
        </PropertiesForContactsProvider>
      </ContactFormProvider>
    );
  }

  return <Loader />;
};

export default Contact;
